import i18next from 'i18next'
import * as React from 'react'
import { datalayerHandler } from '../../../utils/datalayer'
import { useInView } from '../../../utils/hooks/hooks/useInView'
import { Image, ImageBase, ImageOptions } from '../../../utils/images'
import { Link } from '../../../utils/links'
import { maybeToMailPlusLink } from '../../../utils/mailplus'
import { linkGridBlockClasses, relatedActualitiesGridBlockClasses } from '../../../utils/style'
import { convertReactNodeToString, getClassName, htmlDecode, unEntity } from '../../../utils/utils'
import { ActionProps, InviewProps, SVGIconProps } from './blocksState'

export type LinkGridBlock = {
  index: number
  withImages: boolean
  imageOptions?: ImageOptions
  items: LinkGridItemProps[]
  title?: React.ReactNode
  intro?: string
  itemsSVGIcon?: SVGIconProps
  primaryAction?: ActionProps
  inview?: InviewProps
  noItemsMessage?: string
  isRelatedActualities?: boolean
  isEventBlock?: boolean
  eventCount?: number
  teaser?: boolean
}

type LinkGridItemProps = {
  title: string
  label?: string
  intro: string
  link?: {
    url: string
    title: string
  }
  image?: ImageBase
  date?: Date
  booster?: string | null
  location?: string | null
}

export type LinkGridBlockStyle = Partial<{
  blockClassName: string
  inviewClassName: string
  innerClassName: string
  headerClassName: string
  titleClassName: string
  introClassName: string
  contentClassName: string
  emptyClassName: string
  listClassName: string
  gridItemClassName: string
  gridArticleClassName: string
  gridWithLinkClassName: string
  gridWithoutLinkClassName: string
  gridHeaderClassName: string
  gridContentClassName: string
  gridTitleClassName: string
  gridLabelClassName: string
  gridIntroClassName: string
  gridFauxLinkWrapperClassName: string
  gridFauxLinkClassName: string
  gridFauxLinkLabelClassName: string
  gridFauxLinkIconClassName: string
  gridVisualClassName: string
  gridImageClassName: string
  gridPictureClassName: string
  footerClassName: string
  clickableWrapperClassName: string
  clickableClassName: string
  clickableLabelClassName: string
  clickableIconClassName: string
  iconId: string
  iconFauxLinkId: string
}>

function getBlockClasses(props: LinkGridBlock) {
  if (props.isRelatedActualities != undefined && props.isRelatedActualities == true) {
    return relatedActualitiesGridBlockClasses(props.withImages);
  }
  return linkGridBlockClasses(props.withImages);
}

export function LinkGridBlockLayout(props: LinkGridBlock) {
  const { inview, index, items, intro, primaryAction, title, withImages } = props
  const classes = getBlockClasses(props)
  const { inView, ref } = useInView(
    { threshold: 0.1 },
    (inview) => console.debug("LinkGridBlockLayout inView:", inview),
    !inview?.triggerOnce
  )
  const inviewClassName = getClassName(classes, 'blockClassName', 'container') + ' ' + (inView ? getClassName(classes, 'inviewClassName', 'inview') : '')
  return (
    <section key={`block-${index}`} id={`io-${index}`} ref={ref} className={inviewClassName}>
      <div className={getClassName(classes, 'innerClassName', 'py-5')}>
        {(title || intro) && (
          <header
            className={getClassName(classes, 'headerClassName', 'text-center')}
          >
            {title && (
              <h2
                className={getClassName(classes, 'titleClassName', 'mb-3')}
                id={`${index}`}
              >
                {title}
              </h2>
            )}
            {intro && (
              <p className={getClassName(classes, 'introClassName', '')}>
                {intro}
              </p>
            )}
          </header>
        )}

        {(!items || items.length == 0) && props.noItemsMessage && (
          <div className={getClassName(classes, 'contentClassName', '')}>
            <p className={getClassName(classes, 'emptyClassName', '')}>
              {props.noItemsMessage}
            </p>
          </div>
        )}

        {items && items.length > 0 && (
          <>
            <div className={getClassName(classes, 'contentClassName', '')}>
              <ul
                className={getClassName(
                  classes,
                  'listClassName',
                  'row list-unstyled mb-0'
                )}
              >
                {items.map((item, i) => (
                  <li
                    key={`grid-${index}-${i}`}
                    className={getClassName(
                      classes,
                      'gridItemClassName',
                      'col-md-6 col-lg-4 py-3'
                    )}
                  >
                    <article
                      className={getClassName(
                        classes,
                        'gridArticleClassName',
                        ''
                      )}
                    >
                      {item.link ? (
                        <Link
                          url={item.link.url}
                          title={item.link.title}
                          className={getClassName(
                            classes,
                            'gridWithLinkClassName',
                            'd-flex flex-column-reverse text-secondary'
                          )}
                        >
                          <LinkGridItemLayout
                            {...item}
                            imageOptions={
                              props.withImages ? props.imageOptions : undefined
                            }
                            classes={classes}
                          />
                        </Link>
                      ) : (
                        <div
                          className={getClassName(
                            classes,
                            'gridWithoutLinkClassName',
                            'd-flex flex-column-reverse'
                          )}
                        >
                          <LinkGridItemLayout
                            {...item}
                            imageOptions={
                              props.withImages ? props.imageOptions : undefined
                            }
                            classes={classes}
                          />
                        </div>
                      )}
                    </article>
                  </li>
                ))}
              </ul>
            </div>
            {props.isEventBlock && props.teaser && props.eventCount && props.eventCount > 3 ?
              <footer className="block__footer block__clickables">
                <Link url={"/evenementen"} className="button button--secondary">{i18next.t('eventsOverview:AllEventsLink')}</Link>
              </footer> : <></>
            }
          </>
        )}

        {primaryAction && primaryAction.title && (
          <footer
            className={getClassName(classes, 'footerClassName', 'text-center')}
          >
            <div
              className={getClassName(
                classes,
                'clickableWrapperClassName',
                'd-inline-block'
              )}
            >
              <Link
                url={primaryAction.url}
                title={primaryAction.title}
                className={getClassName(
                  classes,
                  'clickableClassName',
                  'btn btn-dark d-inline-flex'
                )}
                onClick={() =>
                  datalayerHandler('link-grid', undefined, undefined, {
                    kind: 'link',
                    title: convertReactNodeToString(title),
                    link: maybeToMailPlusLink(primaryAction.url),
                    label: primaryAction.title
                  })
                }
              >
                <span
                  className={getClassName(
                    classes,
                    'clickableLabelClassName',
                    ''
                  )}
                >
                  {htmlDecode(primaryAction.title)}
                </span>
                {primaryAction.SVGIcon && (
                  <svg
                    className={getClassName(
                      classes,
                      'clickableIconClassName',
                      'ml-3'
                    )}
                    aria-hidden="true"
                    width={
                      primaryAction.SVGIcon.width
                        ? primaryAction.SVGIcon.width
                        : '24px'
                    }
                    height={
                      primaryAction.SVGIcon.height
                        ? primaryAction.SVGIcon.height
                        : '24px'
                    }
                  >
                    <use
                      xlinkHref={getClassName(
                        classes,
                        'iconId',
                        '#icon--chevron-right-large'
                      )}
                    />
                  </svg>
                )}
              </Link>
            </div>
          </footer>
        )}
      </div>
    </section>
  )
}

const LinkGridItemLayout = (
  props: LinkGridItemProps & {
    itemsSVGIcon?: SVGIconProps
    imageOptions?: ImageOptions
    classes: LinkGridBlockStyle
  }
) => {
  const {
    intro,
    title,
    image,
    label,
    link,
    itemsSVGIcon,
    imageOptions,
    classes,
    date,
    booster,
    location
  } = props
  return (
    <>
      {(title || label || intro || (link && link.title)) && (
        <div
          className={getClassName(
            classes,
            'gridContentClassName',
            'card-body px-0 pb-0'
          )}
        >
          <div className="grid__text">
            {(title || label) && (
              <header
                className={getClassName(classes, 'gridHeaderClassName', '')}
              >
                {label && (
                  <span
                    className={getClassName(
                      classes,
                      'gridLabelClassName',
                      'mb-2'
                    )}
                  >
                    {htmlDecode(label)}
                  </span>
                )}

                {title && (
                  <h3
                    className={getClassName(
                      classes,
                      'gridTitleClassName',
                      'mb-2'
                    )}
                  >
                    {htmlDecode(title)}
                  </h3>
                )}
              </header>
            )}

            {intro && (
              <p className={getClassName(classes, 'gridIntroClassName', '')}>
                {unEntity(intro)}
              </p>
            )}
          </div>

          {(booster || date || location) && (
            <div className="grid__meta">
              {booster && (
                <span className="block__booster text--xxs">{booster}</span>
              )}

              {date && (
                <div className="block__meta text--xs">
                  <span>
                    {new Date(date).toLocaleString('nl-NL', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </span>
                </div>
              )}

              {location && <div className="block__meta text--xs">{location}</div>}
            </div>
          )}

          {link && link.title && (
            <footer
              className={getClassName(
                classes,
                'gridFauxLinkWrapperClassName',
                'd-inline-flex align-items-center'
              )}
            >
              <div
                className={getClassName(
                  classes,
                  'gridFauxLinkClassName',
                  'btn btn-link px-0'
                )}
              >
                <span
                  className={getClassName(
                    classes,
                    'gridFauxLinkLabelClassName',
                    ''
                  )}
                >
                  {htmlDecode(link.title)}
                </span>
                {itemsSVGIcon && (
                  <svg
                    className={getClassName(
                      classes,
                      'gridFauxLinkIconClassName',
                      'ml-3'
                    )}
                    aria-hidden="true"
                    width={itemsSVGIcon.width ? itemsSVGIcon.width : '24px'}
                    height={itemsSVGIcon.height ? itemsSVGIcon.height : '24px'}
                  >
                    <use
                      xlinkHref={getClassName(
                        classes,
                        'iconFauxLinkId',
                        '#icon--chevron-right-large'
                      )}
                    />
                  </svg>
                )}
              </div>
            </footer>
          )}
        </div>
      )}

      {image && imageOptions && image.urls.some(url => !!url) && (
        <div
          className={getClassName(
            classes,
            'gridVisualClassName',
            'card-img-top'
          )}
        >
          <Image
            {...imageOptions}
            {...image}
            classNames={{
              image: getClassName(classes, 'gridImageClassName', 'w-100'),
              picture: getClassName(classes, 'gridPictureClassName', '')
            }}
          />
        </div>
      )}
    </>
  )
}
